import { useNavigation } from '@react-navigation/native';
import React from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { welcomeClubs } from '@/assets/images';
import { Stack } from '@/components/layout';
import { Button, Image, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import {
  AuthBrandLogo,
  AuthLegalPopup,
  AuthPrimaryButton,
  AuthScreenBackground,
  AuthTermsText,
} from '@/features/auth/components';
import { useLegalPopup } from '@/features/auth/hooks/use-legal-popup';
import { createStyles, useScaling } from '@/hooks';
import type { UnAuthStackScreenProps } from '@/navigations/types';
import { colors, guardianColors } from '@/theme/colors';

export function WelcomeScreen() {
  const { scale } = useScaling();
  const insets = useSafeAreaInsets();
  // `useSafeAreaInsets().bottom` under-reports the navigation bar on some Android
  // tablets in landscape (returns 0 even with a 32dp gesture bar), so the content
  // ends up tucked under the system bar. `useBottomInsetSmart()` falls back to the
  // real navigationBarHeight from the native module, giving a reliable reserve.
  const styles = useStyles();
  const navigation = useNavigation<UnAuthStackScreenProps<'Welcome'>['navigation']>();
  const legal = useLegalPopup();

  const strings = STRINGS.auth.welcome;

  const handleCreateAccount = () => {
    navigation.navigate('SignUp');
  };

  const handleLogin = () => {
    navigation.navigate('Login');
  };

  const top = insets.top > 0 ? insets.top + scale(26) : scale(40);

  return (
    <AuthScreenBackground>
      <Stack
        flex={1}
        align="center"
        width={scale({ phone: 335, tablet: 342 })}
        paddingX={0}
        style={{
          paddingTop: top,
          // Reserve the real bottom safe area / nav bar, then add the design gap on
          // top so the terms never sit under the system bar.
          paddingBottom: insets.bottom + scale(insets.bottom <= 0 ? 8 : 0),
        }}>
        {/* Logo + hero */}
        <AuthBrandLogo width={200} height={109} resizeMode="contain" portrait />
        <Stack marginTop={scale({ phone: 50, tablet: 20 })} backgroundColor="">
          <Image
            source={welcomeClubs}
            width={scale(308)}
            height={scale(248)}
            resizeMode="contain"
          />
        </Stack>

        {/* Tagline + accent line */}
        <Text
          variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
          style={styles.tagline}
          accessibilityRole="header">
          {strings.tagline}
        </Text>
        <Stack style={styles.accentLine} />
        {/* Flexible spacer pushes the actions to the bottom without overlapping */}
        <Stack flex={1} />
        {/* Actions + terms */}
        <Stack align="center" width="100%">
          <AuthPrimaryButton onPress={handleCreateAccount}>
            {strings.buttonCreateAccount}
          </AuthPrimaryButton>

          <Button onPress={handleLogin} style={styles.secondaryButton}>
            <Button.Text
              variant={'Font Guardian App/Subtitle/Subtitle 14 Semibold'}
              style={styles.secondaryButtonText}>
              {strings.buttonLogin}
            </Button.Text>
          </Button>

          <AuthTermsText
            prefix={strings.termsPrefix}
            breakAfterPrefix
            termsOfService={strings.termsOfService}
            conjunction={strings.termsConjunction}
            privacyPolicy={strings.privacyPolicy}
            onPressTermsOfService={legal.openTerms}
            onPressPrivacyPolicy={legal.openPrivacy}
            style={styles.terms}
          />
        </Stack>
      </Stack>

      <AuthLegalPopup visible={legal.visible} document={legal.document} onClose={legal.close} />
    </AuthScreenBackground>
  );
}

const useStyles = createStyles(({ scale }) => ({
  tagline: {
    marginTop: scale(30),
    color: colors['10'],
    textAlign: 'center',
    width: '100%',
  },
  accentLine: {
    marginTop: scale(9),
    width: scale(44),
    height: scale(4),
    borderRadius: scale(3),
    backgroundColor: guardianColors['Main/Main'],
  },
  secondaryButton: {
    width: '100%',
    borderRadius: scale(8),
    backgroundColor: colors['0'],
    marginTop: scale(20),
  },
  secondaryButtonText: {
    color: guardianColors['Main/Main'],
  },
  terms: {
    marginTop: scale({ phone: 24, tablet: 30 }),
  },
}));

export default WelcomeScreen;
